import type React from 'react';
import { colors } from '../theme/colors';
import { calculateProgressState, getProgressColor } from '../utils/progressUtils';

export interface CircularProgressProps {
  /** Current value */
  value: number;
  /** Maximum value (e.g., FTE hours) */
  max: number;
  /** Size of the progress circle in pixels */
  size?: number;
  /** Stroke width of the progress arc */
  strokeWidth?: number;
  /** Color scheme for different progress states */
  colorScheme?: {
    under?: string;
    near?: string;
    overtime?: string;
    default?: string;
  };
  /** Thresholds for color changes (as percentages of max) */
  thresholds?: {
    near?: number; // e.g., 0.9 for 90%
    overtime?: number; // e.g., 1.0 for 100%
  };
  /** Whether to show overtime beyond the semicircle */
  allowOvertime?: boolean;
  /** Maximum overtime as percentage of max (e.g., 1.5 for 150%) */
  maxOvertime?: number;
  /** Custom className for styling */
  className?: string;
  /** Custom style object */
  style?: React.CSSProperties;
}

export const CircularProgress: React.FC<CircularProgressProps> = ({
  value,
  max,
  size = 120,
  strokeWidth = 8,
  colorScheme = {
    under: colors.accent.yellow,
    near: colors.accent.green,
    overtime: colors.accent.blue,
    default: colors.accent.green,
  },
  thresholds = {
    near: 0.9,
    overtime: 1.0,
  },
  allowOvertime = true,
  maxOvertime = 1.5,
  className,
  style,
}) => {
  // Calculate progress state using centralized utility
  const progressState = calculateProgressState(value, max, thresholds);
  const strokeColor = getProgressColor(progressState, colorScheme);

  // Calculate arc parameters with safety checks
  const progress = Number.isFinite(progressState.progress) ? progressState.progress : 0;
  const maxArcAngle = allowOvertime
    ? Math.min(progress * Math.PI, Math.PI * maxOvertime)
    : Math.min(progress * Math.PI, Math.PI);

  const startAngle = -Math.PI / 2;
  const endAngle = startAngle + maxArcAngle;

  // Convert to SVG path
  const radius = (size - strokeWidth) / 2;
  const centerX = size / 2;
  const centerY = size / 2;

  const startX = centerX + radius * Math.cos(startAngle);
  const startY = centerY + radius * Math.sin(startAngle);
  const endX = centerX + radius * Math.cos(endAngle);
  const endY = centerY + radius * Math.sin(endAngle);

  // Ensure all values are finite before using in SVG
  const safeRadius = Number.isFinite(radius) ? radius : 0;
  const safeStartX = Number.isFinite(startX) ? startX : centerX;
  const safeStartY = Number.isFinite(startY) ? startY : centerY;
  const safeEndX = Number.isFinite(endX) ? endX : centerX;
  const safeEndY = Number.isFinite(endY) ? endY : centerY;
  const safeLargeArcFlag = Number.isFinite(maxArcAngle) && maxArcAngle > Math.PI ? 1 : 0;

  return (
    <div
      className={className}
      style={{
        position: 'relative',
        width: size,
        height: size,
        ...style,
      }}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        style={{
          overflow: 'visible',
          transform: 'rotate(-90deg)',
        }}
      >
        {/* FTE Reference Line (semicircle from 0 to 180 degrees) */}
        <path
          d={`M ${centerX} ${centerY - safeRadius} A ${safeRadius} ${safeRadius} 0 0 1 ${centerX} ${centerY + safeRadius}`}
          fill="none"
          stroke={colors.neutral.grey[300]}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />

        {/* Dynamic Progress Arc */}
        <path
          d={`M ${safeStartX} ${safeStartY} A ${safeRadius} ${safeRadius} 0 ${safeLargeArcFlag} 1 ${safeEndX} ${safeEndY}`}
          fill="none"
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          style={{
            transition: 'd 0.3s ease',
          }}
        />
      </svg>
    </div>
  );
};

export default CircularProgress;
